import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { resolveImageUrl } from "../lib/api";
import { Button } from "./ui/button";
import { Users, UserPlus, UserMinus, Sparkles } from "lucide-react";

function PeerRow({ peer, onChange }) {
  const [busy, setBusy] = useState(false);
  const [following, setFollowing] = useState(peer.is_followed);

  const toggle = async () => {
    setBusy(true);
    try {
      if (following) {
        await api.delete(`/follow/${peer.user_id}`);
        setFollowing(false);
      } else {
        await api.post(`/follow/${peer.user_id}`);
        setFollowing(true);
      }
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`similar-row-${peer.user_id}`}
      className="flex items-center gap-3 p-3 rounded-xl bg-zinc-900/60 ring-1 ring-white/5 hover:ring-rose-500/20 transition-colors"
    >
      <Link to={peer.profile_slug ? `/collector/${peer.profile_slug}` : "#"} className="flex items-center gap-3 flex-1 min-w-0">
        {peer.profile_photo ? (
          <img src={resolveImageUrl(peer.profile_photo)} alt="" className="w-10 h-10 rounded-full object-cover ring-1 ring-zinc-800" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-zinc-800 grid place-items-center text-sm text-zinc-300">
            {(peer.name || "?")[0]}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-zinc-100 text-sm font-medium truncate">{peer.name}</p>
          <p className="text-zinc-500 text-xs flex items-center gap-1.5">
            <Sparkles className="w-3 h-3 text-amber-400" />
            {peer.overlap_count} shared · {Math.round(peer.jaccard * 100)}% match
            {peer.shared_top_brand && <span className="text-amber-400/80"> · {peer.shared_top_brand}</span>}
          </p>
        </div>
      </Link>
      <Button
        size="sm"
        variant={following ? "ghost" : "default"}
        disabled={busy}
        onClick={toggle}
        data-testid={`similar-follow-btn-${peer.user_id}`}
        className={following ? "text-zinc-400 hover:text-rose-400" : "bg-rose-600 hover:bg-rose-500 text-white"}
      >
        {following ? <UserMinus className="w-3.5 h-3.5 mr-1" /> : <UserPlus className="w-3.5 h-3.5 mr-1" />}
        {following ? "Following" : "Follow"}
      </Button>
    </div>
  );
}

export default function SimilarCollectorsPanel({ limit = 6, compact = false, className = "" }) {
  const [data, setData] = useState(null);

  const load = () => {
    api.get(`/collectors/similar?limit=${limit}`).then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [limit]);

  if (!data) return null;
  if (data.empty_reason === "not_enough_collection") {
    return compact ? null : (
      <div className={`bg-zinc-900 ring-1 ring-white/5 rounded-2xl p-5 ${className}`} data-testid="similar-empty-collection">
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-4 h-4 text-rose-400" />
          <h3 className="font-heading text-sm font-semibold text-zinc-100">Collectors like you</h3>
        </div>
        <p className="text-zinc-400 text-xs">Add at least 3 items to your collection to see similar collectors.</p>
      </div>
    );
  }
  if (!data.results || data.results.length === 0) return null;

  return (
    <div data-testid="similar-collectors-panel" className={`bg-zinc-900 ring-1 ring-white/5 rounded-2xl p-5 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-rose-400" />
          <h3 className="font-heading text-sm font-semibold text-zinc-100">Collectors like you</h3>
        </div>
        {data.your_top_brand && (
          <span className="text-[10px] text-zinc-500 uppercase tracking-widest">Top brand · {data.your_top_brand}</span>
        )}
      </div>
      <div className="space-y-2">
        {data.results.map((peer) => (
          <PeerRow key={peer.user_id} peer={peer} onChange={load} />
        ))}
      </div>
    </div>
  );
}
